import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import KpiTile from './charts/KpiTile';
import MiniBarChart from './charts/MiniBarChart';
import LiveTicker from './charts/LiveTicker';
import OrderDetailModal from './OrderDetailModal';
import StuckOrdersModal from './StuckOrdersModal';
import AnalyticsRangePanel from './AnalyticsRangePanel';
import './AdminAnalyticsOverview.css';

// Premium-fintech admin overview. Hits /api/admin/analytics/overview on a
// 10s poll AND refreshes immediately when a notification:new socket event
// fires (so the screen updates the moment something happens). All chart
// primitives are inline SVG — no chart library, ~0kb extra bundle.

const REFRESH_MS = 10_000;

const fmtUsd = (n) => {
  const num = Number(n);
  if (!isFinite(num)) return '—';
  if (Math.abs(num) >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`;
  if (Math.abs(num) >= 1_000)     return `$${(num / 1_000).toFixed(2)}k`;
  return `$${num.toFixed(2)}`;
};
const fmtInt = (n) => Number(n || 0).toLocaleString();
const fmtBnb = (n) => `${Number(n || 0).toFixed(4)}`;
const shortWallet = (a) => (a && a.length > 12 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a);
const timeAgo = (d) => {
  if (!d) return '';
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const STATUS_COLORS = {
  Completed:    '#34d399',
  'In progress':'#fbbf24',
  Processing:   '#fbbf24',
  Pending:      '#00e0ff',
  Canceled:     '#f87171',
  Refunded:     '#a78bfa',
};

const AdminAnalyticsOverview = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const inflightRef = useRef(false);
  const navigate = useNavigate();
  const [openOrderId, setOpenOrderId] = useState(null);
  const [stuckOpen, setStuckOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(null);
  const [actionMsg, setActionMsg] = useState({});

  // Navigate to an admin tab without a full reload (same pattern as alert clicks).
  const goToTab = useCallback((tab) => {
    navigate(`/admin?tab=${tab}`);
    window.dispatchEvent(new CustomEvent('admin:setTab', { detail: { tab } }));
  }, [navigate]);

  // Route an alert click through React Router (no full reload — keeps the
  // wallet/auth context alive so AdminRoute doesn't briefly 403 the user).
  // The alert link format from the backend is `/admin?tab=KEY` — we extract
  // the tab key and dispatch a window event the AdminDashboard listens to.
  const handleAlertClick = useCallback((e, link) => {
    if (!link) return;
    e.preventDefault();
    try {
      const url = new URL(link, window.location.origin);
      const tab = url.searchParams.get('tab');
      if (url.pathname.startsWith('/admin') && tab) {
        navigate(url.pathname + url.search, { replace: false });
        window.dispatchEvent(new CustomEvent('admin:setTab', { detail: { tab } }));
      } else {
        navigate(url.pathname + url.search);
      }
    } catch (_) {
      navigate(link);
    }
  }, [navigate]);

  const load = useCallback(async () => {
    if (inflightRef.current) return;
    inflightRef.current = true;
    try {
      const res = await api.get('/admin/analytics/overview');
      setData(res.data);
      setError('');
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load analytics');
    } finally {
      inflightRef.current = false;
      setLoading(false);
    }
  }, []);

  // Inline alert actions: "Refresh now" (POST) or "Fund operator" (open scan).
  // stopPropagation so the button doesn't also trigger the alert's navigate.
  const handleAlertAction = useCallback(async (e, a) => {
    e.preventDefault();
    e.stopPropagation();
    const action = a?.action;
    if (!action) return;
    if (action.type === 'fund_operator' && action.url) {
      window.open(action.url, '_blank', 'noopener');
      return;
    }
    if (action.type === 'refresh_stuck' && action.endpoint) {
      setActionBusy(a.key);
      setActionMsg((m) => ({ ...m, [a.key]: '' }));
      try {
        // endpoint is '/api/smm/...'; the api client is already based at '/api'.
        const path = action.endpoint.replace(/^\/api/, '');
        const res = await api.post(path, {});
        const n = res?.data?.updated ?? 0;
        setActionMsg((m) => ({ ...m, [a.key]: `Refreshed ${n} order${n === 1 ? '' : 's'}` }));
        await load();
      } catch (err) {
        setActionMsg((m) => ({ ...m, [a.key]: err?.response?.data?.message || 'Refresh failed' }));
      } finally {
        setActionBusy(null);
      }
    }
  }, [load]);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  // Listen for the in-app notification socket event (NotificationBell opens
  // its own connection; we tap window for cross-component coordination
  // without coupling to that component).
  useEffect(() => {
    const handler = () => load();
    window.addEventListener('phoneai:notification', handler);
    return () => window.removeEventListener('phoneai:notification', handler);
  }, [load]);

  if (loading && !data) {
    return (
      <div className="analytics-overview analytics-overview--loading">
        <div className="analytics-skel-grid">
          {[1, 2, 3, 4].map((i) => <div key={i} className="analytics-skel-tile" />)}
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="analytics-overview">
        <div className="analytics-error">⚠ {error}</div>
      </div>
    );
  }

  if (!data || !data.kpis || !data.series) return null; // guard a malformed payload
  const { kpis, series, recent_orders: recentOrders = [], alerts = [], generated_at: generatedAt } = data;

  const orderCountSeries = (series.orders_per_hour_24h || []).map((p) => p.count);
  const revenueDaySeries = (series.revenue_per_day_14d || []).map((p) => ({
    label: String(p.d || '').slice(5),
    value: Number(p.revenue || 0),
  }));
  const statusEntries = Object.entries(series.status_breakdown_24h || {});
  const statusTotal = statusEntries.reduce((s, [, n]) => s + n, 0);

  const operatorScan = kpis.operator_bnb.operator
    ? `https://bscscan.com/address/${kpis.operator_bnb.operator}`
    : null;

  const v2Pool = kpis.v2_refund_pool || {};
  const v2Scan = v2Pool.address ? `https://bscscan.com/address/${v2Pool.address}` : null;
  const opUsdt = kpis.operator_usdt || {};

  const tickerItems = [
    { key: 'orders24h', label: 'Orders 24h', value: fmtInt(kpis.orders_24h.value),
      hint: 'View SMM orders', onClick: () => goToTab('smm') },
    { key: 'rev24h',    label: 'Revenue 24h', value: fmtUsd(kpis.revenue_24h_usd.value),
      accent: kpis.revenue_24h_usd.delta_pct >= 0 ? 'green' : 'red',
      hint: 'View SMM orders & revenue', onClick: () => goToTab('smm') },
    { key: 'users',     label: 'Users', value: fmtInt(kpis.users_total.value),
      hint: 'View users', onClick: () => goToTab('users') },
    { key: 'gas',       label: 'Op Gas', value: fmtBnb(kpis.operator_bnb.value), suffix: 'BNB',
      accent: kpis.operator_bnb.status === 'critical' ? 'red'
            : kpis.operator_bnb.status === 'warn' ? 'amber' : 'green',
      hint: operatorScan ? 'Open operator wallet on BscScan' : undefined,
      onClick: operatorScan ? () => window.open(operatorScan, '_blank', 'noopener') : undefined },
    { key: 'opUsdt',    label: 'Op USDT', value: fmtUsd(opUsdt.value), suffix: 'USDT',
      hint: operatorScan ? 'Operator wallet on BscScan' : undefined,
      onClick: operatorScan ? () => window.open(operatorScan, '_blank', 'noopener') : undefined },
    { key: 'refundPool', label: 'Refund Pool',
      value: v2Pool.configured ? `$${Number(v2Pool.value || 0).toFixed(2)}` : 'off',
      suffix: v2Pool.configured ? 'USDT' : undefined,
      accent: v2Pool.status === 'critical' ? 'red'
            : v2Pool.status === 'warn' ? 'amber'
            : v2Pool.status === 'unconfigured' ? 'amber' : 'green',
      hint: v2Scan ? 'v2 escrow on BscScan' : 'Phase 2 escrow not configured',
      onClick: v2Scan ? () => window.open(v2Scan, '_blank', 'noopener') : undefined },
    { key: 'vault',     label: 'Vault PnL', value: kpis.vault_platform_earnings_usdt.value, suffix: 'USDT',
      hint: 'PlayVault / game config', onClick: () => goToTab('game-config') },
    { key: 'stuck',     label: 'Stuck', value: fmtInt(kpis.stuck_orders.value),
      accent: kpis.stuck_orders.value > 0 ? 'amber' : 'gold',
      hint: 'Show stuck orders', onClick: () => setStuckOpen(true) },
    { key: 'tickets',   label: 'Open Tickets', value: fmtInt(kpis.open_tickets.value),
      accent: kpis.open_tickets.value > 0 ? 'amber' : 'gold',
      hint: 'View PHONE tickets', onClick: () => goToTab('tickets') },
    { key: 'chats',     label: 'Unread Chats', value: fmtInt(kpis.unread_chats.value),
      accent: kpis.unread_chats.value > 0 ? 'amber' : 'gold',
      hint: 'Open support inbox', onClick: () => goToTab('support') },
  ];

  return (
    <div className="analytics-overview">
      <LiveTicker items={tickerItems} updatedAt={generatedAt} />

      {/* ── KPI grid ────────────────────────────────────────────────── */}
      <div className="analytics-kpi-grid">
        <KpiTile
          label="Orders (24h)"
          value={fmtInt(kpis.orders_24h.value)}
          delta={{ pct: kpis.orders_24h.delta_pct, label: 'vs prior 24h' }}
          series={orderCountSeries}
          hint="View SMM orders"
          onClick={() => goToTab('smm')}
        />
        <KpiTile
          label="Revenue (24h)"
          value={fmtUsd(kpis.revenue_24h_usd.value)}
          delta={{ pct: kpis.revenue_24h_usd.delta_pct, label: 'vs prior 24h' }}
          series={(series.orders_per_hour_24h || []).map((p) => p.revenue)}
          hint="View SMM orders & revenue"
          onClick={() => goToTab('smm')}
        />
        <KpiTile
          label="Total Users"
          value={fmtInt(kpis.users_total.value)}
          delta={{ abs: `+${kpis.users_total.delta_24h}`, label: 'new 24h' }}
          hint="View users"
          onClick={() => goToTab('users')}
        />
        <KpiTile
          label="Operator Gas"
          value={fmtBnb(kpis.operator_bnb.value)}
          suffix="BNB"
          status={kpis.operator_bnb.status}
          hint={operatorScan ? 'Open operator wallet on BscScan' : undefined}
          onClick={operatorScan ? () => window.open(operatorScan, '_blank', 'noopener') : undefined}
        />
        <KpiTile
          label="Operator USDT"
          value={fmtUsd(opUsdt.value)}
          suffix="USDT"
          hint={operatorScan ? 'Operator wallet on BscScan' : undefined}
          onClick={operatorScan ? () => window.open(operatorScan, '_blank', 'noopener') : undefined}
        />
        <KpiTile
          label="Refund Pool (v2)"
          value={v2Pool.configured ? `$${Number(v2Pool.value || 0).toFixed(2)}` : 'Not configured'}
          suffix={v2Pool.configured ? 'USDT' : undefined}
          status={v2Pool.status === 'critical' ? 'critical'
                : v2Pool.status === 'warn' ? 'warn'
                : v2Pool.status === 'unconfigured' ? 'warn'
                : 'ok'}
          hint={v2Scan ? 'Open v2 escrow on BscScan' :
                v2Pool.configured ? undefined :
                'Set USDT_ESCROW_V2_ADDRESS in backend .env to enable'}
          onClick={v2Scan ? () => window.open(v2Scan, '_blank', 'noopener') : undefined}
        />
      </div>

      {/* ── Mid row: charts ─────────────────────────────────────────── */}
      <div className="analytics-mid">
        <section className="analytics-panel analytics-panel--wide">
          <div className="analytics-panel-head">
            <span className="analytics-panel-title">Revenue · last 14 days</span>
            <span className="analytics-panel-meta">
              Total {fmtUsd(revenueDaySeries.reduce((s, p) => s + p.value, 0))}
            </span>
          </div>
          <MiniBarChart data={revenueDaySeries} height={140} color="#00e0ff" />
        </section>

        <section className="analytics-panel">
          <div className="analytics-panel-head">
            <span className="analytics-panel-title">Order status · 24h</span>
            <span className="analytics-panel-meta">{statusTotal} total</span>
          </div>
          {statusTotal === 0 ? (
            <div className="analytics-panel-empty">No orders in the last 24h.</div>
          ) : (
            <div className="analytics-status-list">
              {statusEntries.map(([status, count]) => {
                const pct = (count / statusTotal) * 100;
                return (
                  <div key={status} className="analytics-status-row">
                    <span className="analytics-status-label">
                      <span className="analytics-status-dot"
                            style={{ background: STATUS_COLORS[status] || '#888' }} />
                      {status}
                    </span>
                    <span className="analytics-status-bar-wrap">
                      <span className="analytics-status-bar"
                            style={{ width: `${pct}%`, background: STATUS_COLORS[status] || '#888' }} />
                    </span>
                    <span className="analytics-status-count">{count}</span>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {/* ── Bottom row: alerts + recent orders ──────────────────────── */}
      <div className="analytics-bottom">
        <section className="analytics-panel">
          <div className="analytics-panel-head">
            <span className="analytics-panel-title">Alerts</span>
            <span className="analytics-panel-meta">
              {alerts.length === 0 ? 'All clear' : `${alerts.length} active`}
            </span>
          </div>
          {alerts.length === 0 ? (
            <div className="analytics-panel-empty">Nothing needs your attention right now.</div>
          ) : (
            <div className="analytics-alerts">
              {alerts.map((a) => (
                <a key={a.key} href={a.link || '#'}
                   onClick={(e) => handleAlertClick(e, a.link)}
                   title="Opens where you can resolve this — the alert clears itself once fixed"
                   className={`analytics-alert analytics-alert--${a.severity}`}>
                  <span className="analytics-alert-mark">
                    {a.severity === 'critical' ? '!' : a.severity === 'warn' ? '⚠' : 'i'}
                  </span>
                  <span className="analytics-alert-body">
                    <span className="analytics-alert-title">{a.title}</span>
                    {a.body && <span className="analytics-alert-text">{a.body}</span>}
                    {actionMsg[a.key] && (
                      <span className="analytics-alert-actionmsg">{actionMsg[a.key]}</span>
                    )}
                  </span>
                  {a.action ? (
                    <button
                      type="button"
                      className="analytics-alert-action"
                      onClick={(e) => handleAlertAction(e, a)}
                      disabled={actionBusy === a.key}
                    >
                      {actionBusy === a.key ? '…' : a.action.label}
                    </button>
                  ) : (
                    <span className="analytics-alert-go">→</span>
                  )}
                </a>
              ))}
            </div>
          )}
          {alerts.length > 0 && (
            <div className="analytics-alert-foot">
              Live status — clicking takes you to where you fix it. Each clears on its own once resolved (refreshes ~10s).
            </div>
          )}
        </section>

        <section className="analytics-panel analytics-panel--wide">
          <div className="analytics-panel-head">
            <span className="analytics-panel-title">Recent orders</span>
            <button
              type="button"
              className="analytics-seeall"
              onClick={() => {
                navigate('/admin?tab=smm');
                window.dispatchEvent(new CustomEvent('admin:setTab', { detail: { tab: 'smm' } }));
              }}
            >
              See all →
            </button>
          </div>
          {(!recentOrders || recentOrders.length === 0) ? (
            <div className="analytics-panel-empty">No orders yet.</div>
          ) : (
            <div className="analytics-orders">
              <div className="analytics-orders-row analytics-orders-head">
                <span>Time</span><span>Wallet</span><span>Service</span>
                <span>Qty</span><span>Amount</span><span>Status</span>
              </div>
              {recentOrders.map((o) => (
                <div
                  key={o.db_id}
                  role="button"
                  tabIndex={0}
                  className="analytics-orders-row analytics-orders-row--clickable"
                  onClick={() => setOpenOrderId(o.db_id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenOrderId(o.db_id); } }}
                  title="Click for full order detail + notes"
                >
                  <span className="analytics-orders-time">{timeAgo(o.created_at)}</span>
                  <span className="analytics-orders-wallet"><code>{shortWallet(o.wallet)}</code></span>
                  <span className="analytics-orders-service"
                        title={o.service_name}>{o.service_name || '—'}</span>
                  <span>{fmtInt(o.quantity)}</span>
                  <span className="analytics-orders-amt">{fmtUsd(o.charged_usd)}</span>
                  <span>
                    <span className="analytics-orders-status"
                          style={{
                            color: STATUS_COLORS[o.status] || '#aaa',
                            borderColor: (STATUS_COLORS[o.status] || '#aaa') + '55',
                            background: (STATUS_COLORS[o.status] || '#aaa') + '12',
                          }}>
                      {o.status || '?'}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <AnalyticsRangePanel scope="admin" />

      <StuckOrdersModal
        open={stuckOpen}
        onClose={() => { setStuckOpen(false); load(); }}
        onOpenOrder={(id) => { setStuckOpen(false); setOpenOrderId(id); }}
      />

      <OrderDetailModal
        orderId={openOrderId}
        onClose={() => { setOpenOrderId(null); load(); }}
      />
    </div>
  );
};

export default AdminAnalyticsOverview;
